import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { MessageSquareText, Plus, Trash2, Save, Headset } from "lucide-react";
import { V } from "@/lib/vocab";

export default function QuickReplies({ embedded = false }) {
  const { tenantId } = useDash();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(() => {
    if (!tenantId) return;
    return api.get(`/tenants/${tenantId}/canned`).then((r) => setItems(r.data)).catch(() => {});
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and a message"); return; }
    try {
      await api.post(`/tenants/${tenantId}/canned`, { title, body });
      setTitle(""); setBody(""); load(); toast.success("Quick reply added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const save = async (it) => {
    try { await api.put(`/tenants/${tenantId}/canned/${it.id}`, { title: it.title, body: it.body }); toast.success("Saved"); }
    catch { toast.error("Could not save"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/tenants/${tenantId}/canned/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };
  const edit = (id, field, val) => setItems((arr) => arr.map((x) => x.id === id ? { ...x, [field]: val } : x));

  return (
    <div className={`space-y-6 ${embedded ? "" : "max-w-2xl"}`} data-testid="quick-replies-page">
      {!embedded && <div className="rounded-2xl bg-sand-50 border border-sand-200 p-4">
        <div className="font-heading font-bold text-ink flex items-center gap-2 mb-1"><Headset className="h-4 w-4 text-redline-500" /> What are Quick Replies?</div>
        <p className="text-sm text-muted-foreground">
          These are canned, one-tap responses <span className="font-semibold text-ink">for the human operators</span> who work the live inbox. When an operator is replying to {V.a("person")} in <span className="font-semibold text-ink">Conversations</span>, they can tap one of these to drop the pre-written text into the reply box (and edit it before sending). {V.many("person")} never see this list. Use it for the answers your team types over and over: pool hours, {V.lcs("dues")} instructions, gate codes, common policies.
        </p>
      </div>}

      <div className="bg-white rounded-2xl border border-sand-200 p-5">
        <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><Plus className="h-4 w-4 text-redline-500" /> New quick reply</h3>
        <input data-testid="qr-new-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short title (e.g. Pool hours)"
          className="w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink mb-2" />
        <textarea data-testid="qr-new-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="The full message operators can send…"
          className="w-full rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink resize-y" />
        <div className="flex justify-end mt-3">
          <button data-testid="qr-add-btn" onClick={add} className="flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="text-center text-muted-foreground py-8 flex flex-col items-center gap-2">
            <MessageSquareText className="h-8 w-8 text-sand-300" /> No quick replies yet.
          </div>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`qr-item-${it.id}`} className="bg-white rounded-2xl border border-sand-200 p-4">
            <input value={it.title} onChange={(e) => edit(it.id, "title", e.target.value)}
              className="w-full font-semibold text-ink bg-transparent outline-none border-b border-transparent focus:border-sand-200 pb-1 mb-2" />
            <textarea value={it.body} onChange={(e) => edit(it.id, "body", e.target.value)} rows={2}
              className="w-full text-sm text-ink-700 bg-sand-50 rounded-lg p-2 outline-none focus:ring-1 focus:ring-redline-300 resize-y" />
            <div className="flex justify-end gap-2 mt-2">
              <button data-testid={`qr-save-${it.id}`} onClick={() => save(it)} className="flex items-center gap-1.5 text-xs font-semibold text-ink-700 bg-sand-100 rounded-full px-3 py-1.5 hover:bg-sand-200">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
              <button data-testid={`qr-del-${it.id}`} onClick={() => remove(it.id)} className="flex items-center gap-1.5 text-xs font-semibold text-danger-600 bg-danger-50 rounded-full px-3 py-1.5 hover:bg-danger-100">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
